import { useMemo } from 'react';

import { getMsUntilNextSecond } from '#shared';

import { usePollingQuery } from '../../usePollingQuery';
import { useGetActiveCasesQuery } from '../activeCases.api';
import { useStaleWhileEmpty } from '../useStaleWhileEmpty';

import type { ActiveCase } from '#entities';
import type { PollingQueryResult } from '../types';

const TARGET_SECOND = 5;

const getActiveCasesDelay = () => getMsUntilNextSecond(TARGET_SECOND);

export const useQueryActiveCases = (): PollingQueryResult<ActiveCase[]> => {
  const queryResult = useGetActiveCasesQuery(undefined, {
    pollingInterval: 0,
  });

  const pollingResult = usePollingQuery(queryResult, {
    getDelay: getActiveCasesDelay,
  });

  const stale = useStaleWhileEmpty(
    {
      data: pollingResult.data?.data ?? [],
      generatedAt: pollingResult.data?.generatedAt ?? null,
    },
    pollingResult.lastReceiveTime?.getTime() ?? null,
  );

  return useMemo(
    () => ({
      data: stale.data,
      generatedAt: stale.generatedAt,
      isStale: stale.isStale,
      validation: pollingResult.data?.validation ?? null,
      isLoading: pollingResult.isLoading,
      isFetching: pollingResult.isFetching,
      isError: pollingResult.isError,
      error: pollingResult.error,
      lastSentTime: pollingResult.lastSentTime,
      lastReceiveTime: pollingResult.lastReceiveTime,
    }),
    [pollingResult, stale],
  );
};
